import React, { useMemo } from 'react';
import { WebStatsResponse } from '../../types/dashboard';
import TableSkeleton from '../common/TableSkeleton';
import { calculateWebCredits } from '../../config/costs';

interface Props {
  stats?: WebStatsResponse | null;
  isLoading: boolean;
}

const WebStats: React.FC<Props> = ({ stats, isLoading }) => {
  const sortedUserCosts = useMemo(() => {
    if (!stats?.web_stats.user_costs) return [];
    return [...stats.web_stats.user_costs]
      .filter((user) => {
        const credits = calculateWebCredits(user.input_tokens || 0, user.output_tokens || 0);
        return credits > 0; // Only show users with credit usage > 0
      })
      .sort((a, b) => {
        const costA = calculateWebCredits(a.input_tokens || 0, a.output_tokens || 0);
        const costB = calculateWebCredits(b.input_tokens || 0, b.output_tokens || 0);
        return costB - costA;
      });
  }, [stats?.web_stats.user_costs]);

  const formatTokens = (tokens: number) => {
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(2)}M`;
    }
    return tokens.toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="stats-card hover:border-primary-500/20 group animate-pulse">
        <div className="p-6 space-y-6">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white">Web Stats</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="rounded-lg bg-light-hover dark:bg-dark-hover p-4">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
              </div>
            ))}
          </div>

          <div>
            <h3 className="text-gray-900 dark:text-white font-medium mb-4">User Credits</h3>
            <TableSkeleton 
              columns={7}
              rows={5}
            />
          </div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const totalCredits = calculateWebCredits(
    stats.web_stats.total_input_tokens,
    stats.web_stats.total_output_tokens
  );

  return (
    <div className="stats-card hover:border-primary-500/20 group">
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-start">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white">Web Stats</h2>
          <div className="text-sm text-gray-500">
            {stats.date_range.start_date} - {stats.date_range.end_date}
            {stats.org_id && <span className="ml-2">| Organization ID: {stats.org_id}</span>}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <div className="rounded-lg bg-light-hover dark:bg-dark-hover p-4 group-hover:bg-primary-500/10 dark:group-hover:bg-primary-500/20 transition-colors duration-200">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tasks</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.web_stats.total_tasks}</p>
          </div>
          <div className="rounded-lg bg-light-hover dark:bg-dark-hover p-4 group-hover:bg-primary-500/10 dark:group-hover:bg-primary-500/20 transition-colors duration-200">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Users</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.web_stats.total_users}</p>
          </div>
          {/* Token displays commented out
          <div className="rounded-lg bg-light-hover dark:bg-dark-hover p-4 group-hover:bg-primary-500/10 dark:group-hover:bg-primary-500/20 transition-colors duration-200">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Input Tokens</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{formatTokens(stats.web_stats.total_input_tokens)}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{stats.web_stats.total_input_tokens.toLocaleString()}</p>
          </div>
          <div className="rounded-lg bg-light-hover dark:bg-dark-hover p-4 group-hover:bg-primary-500/10 dark:group-hover:bg-primary-500/20 transition-colors duration-200">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Output Tokens</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{formatTokens(stats.web_stats.total_output_tokens)}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{stats.web_stats.total_output_tokens.toLocaleString()}</p>
          </div>
          */}
          <div className="rounded-lg bg-light-hover dark:bg-dark-hover p-4 group-hover:bg-primary-500/10 dark:group-hover:bg-primary-500/20 transition-colors duration-200">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Credits</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{totalCredits.toFixed(2)}</p>
          </div>
        </div>

        {sortedUserCosts.length > 0 && (
          <div>
            <h3 className="text-gray-900 dark:text-white font-medium mb-4">User Credits</h3>
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden rounded-lg border border-light-border dark:border-dark-border">
                  <div className="overflow-y-auto" style={{ maxHeight: '250px' }}>
                    <table className="w-full divide-y divide-light-border dark:divide-dark-border">
                      <thead>
                        <tr>
                          <th className="sticky top-0 px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider bg-gray-100 dark:bg-[#0A0A0A]">
                            USER ID
                          </th>
                          <th className="sticky top-0 px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider bg-gray-100 dark:bg-[#0A0A0A]">
                            NAME
                          </th>
                          <th className="sticky top-0 px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider bg-gray-100 dark:bg-[#0A0A0A]">
                            EMAIL
                          </th>
                          <th className="sticky top-0 px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider bg-gray-100 dark:bg-[#0A0A0A]">
                            ORGANIZATION
                          </th>
                          <th className="sticky top-0 px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider bg-gray-100 dark:bg-[#0A0A0A]">
                            PROJECTS
                          </th>
                          {/* Token column commented out
                          <th className="sticky top-0 px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider bg-gray-100 dark:bg-[#0A0A0A]">
                            TOKENS (IN/OUT)
                          </th>
                          */}
                          <th className="sticky top-0 px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider bg-gray-100 dark:bg-[#0A0A0A]">
                            CREDITS
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-light-border dark:divide-dark-border bg-light-card dark:bg-dark-card">
                        {sortedUserCosts.map((user) => (
                          <tr key={user.user_id} className="hover:bg-light-hover dark:hover:bg-dark-hover transition-colors duration-200">
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                              {user.user_id}
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900 dark:text-white">{`${user.first_name} ${user.last_name}`}</div>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900 dark:text-white">{user.email}</div>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900 dark:text-white">{user.org_name}</div>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {user.projects_count}
                            </td>
                            {/* Token display commented out
                            <td className="px-6 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900 dark:text-white">
                                {formatTokens(user.input_tokens)} / {formatTokens(user.output_tokens)}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {user.input_tokens.toLocaleString()} / {user.output_tokens.toLocaleString()}
                              </div>
                            </td>
                            */}
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {calculateWebCredits(user.input_tokens || 0, user.output_tokens || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebStats;
